import { Box, Button, Divider, Tooltip, Typography, useTheme } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CgProfile } from "react-icons/cg";
import { FaChartBar, FaCog, FaUserTie } from "react-icons/fa";
import { LuDatabase } from "react-icons/lu";
import { IoHomeOutline, IoLogOut, IoNotificationsOutline } from "react-icons/io5";
import { MdContactSupport } from "react-icons/md";
import { useNavigate, useLocation, Link } from "react-router-dom";
import logo from "../assets/logo/ArivuHeaderWhite.png";
import foot from "../assets/logo/foot.png";
import { useAuth } from "../authentication/AuthProvider";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { Notification } from "../db_access/notification";
import { Logout as LO } from "../db_access/logout";
import { clearUserCache } from "../utilities/queryClient";
import routing from "../routing";

const LeftSidebar = ({ tabs }) => {
  const { t } = useTranslation();
  const { isAdmin, isCompanyAdmin, logout: authLogout, attemptLogout } = useAuth();
  const theme = useTheme();
  const { leftSidebarCollapse, setLeftSidebarCollapse, refresh } = useContextProvider();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      // SECURITY: Clear current user's cached LCA data on logout
      await clearUserCache();
      // Inform backend to clear token (don't wait for it if it fails)
      LO.clear_token().finally(() => {
        authLogout();
        navigate(routing.login);
      });
    } catch (error) {
      console.error("Logout failed", error);
      authLogout();
      navigate(routing.login);
    }
  };

  // Handle smart profile toggle navigation
  const handleProfileClick = () => {
    const currentPath = location.pathname;
    const isOnProfilePage = currentPath.startsWith(routing.profile.root);

    if (isOnProfilePage) {
      // If on profile page, navigate back to previously saved location
      const previousLocation = localStorage.getItem("previousLocationBeforeProfile");
      if (previousLocation && previousLocation !== routing.profile.account_settings) {
        navigate(previousLocation);
      } else {
        // Default fallback if no previous location exists
        navigate(routing.sector.products.dashboard);
      }
    } else {
      // Save current location before navigating to profile
      localStorage.setItem("previousLocationBeforeProfile", currentPath);
      navigate(routing.profile.account_settings);
    }
  };

  const [hasUnreadNotification, setHasUnreadNotification] = useState(false);
  const get_hasUnreadNotification = () => {
    Notification.has_mine_unread()
      .then((res) => res.json())
      .then((data) => setHasUnreadNotification(data));
  };
  useEffect(() => get_hasUnreadNotification(), [location, refresh]);

  const bottomTabs = useMemo(
    () => [
      { label: "Usage", value: "usage", icon: FaChartBar, rootPath: routing.usage.root, path: routing.usage.dashboard, visibility: false, hasRedDot: false },
      {
        label: "Profile",
        subHeader: "Features",
        value: "profile",
        icon: CgProfile,
        rootPath: routing.profile.root,
        path: routing.profile.account_settings,
        visibility: false,
        hasRedDot: hasUnreadNotification,
      },
      { label: "Setting", value: "setting", icon: FaCog, rootPath: routing.setting.root, path: routing.setting.dashboard, visibility: false, hasRedDot: false },
      { label: "Logout", value: "logout", icon: IoLogOut, rootPath: routing.logout.root, path: routing.logout.root, visibility: false, hasRedDot: false },
    ],
    [hasUnreadNotification, isAdmin, isCompanyAdmin]
  );
  return (
    <>
      {/* Placeholder to maintain flex layout space */}
      <Box
        sx={{
          minWidth: leftSidebarCollapse ? 80 : 280,
          flexShrink: 0,
          transition: "min-width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      />
      <Box
        sx={{
          borderRight: "1px solid rgba(255, 255, 255, 0.05)",
          display: "flex",
          flexDirection: "column",
          backgroundColor: theme.palette.background.sidebar,
          width: leftSidebarCollapse ? 80 : 280,
          height: "calc(100vh - 60px)",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 1200,
          transition: "width 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* ── TOP LOGO AREA ── */}
        <Box
          sx={{
            display: "flex",
            height: "78px",
            paddingX: 3,
            alignItems: "center",
            justifyContent: "center",
            borderBottom: `1px solid ${theme.palette.sidebar_divider}`,
          }}
        >
          <Button
            sx={{
              padding: 0,
              minWidth: 0,
              width: "100%",
              justifyContent: leftSidebarCollapse ? "center" : "flex-start",
              transition: "transform 0.2s ease",
              "&:hover": { transform: "scale(1.02)" }
            }}
            onClick={() => setLeftSidebarCollapse((p) => !p)}
            disableRipple
          >
            <img
              src={leftSidebarCollapse ? foot : logo}
              alt="Arivu"
              style={{
                height: leftSidebarCollapse ? "40px" : "40px",
                width: "auto",
                objectFit: "contain"
              }}
            />
          </Button>
        </Box>

        {/* ── SIDEBAR CONTENT ── */}
        <Box
          sx={{
            paddingX: "12px",
            paddingTop: "16px",
            display: "flex",
            flexDirection: "column",
            flex: 1,
            gap: "6px",
            overflowY: "auto",
          }}
        >
          {/* ── HOME ── */}
          <Box>
            <Tooltip title={leftSidebarCollapse ? t("Home") : ""} placement="right">
              <Link to="/home" style={{ textDecoration: "none" }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    paddingY: "10px",
                    paddingX: "14px",
                    borderRadius: "8px",
                    gap: "12px",
                    backgroundColor: location.pathname === "/home" ? theme.palette.primary.main : "transparent",
                    "&:hover": {
                      backgroundColor: location.pathname === "/home" ? theme.palette.primary.main : "rgba(255, 255, 255, 0.05)",
                    },
                  }}
                >
                  <IoHomeOutline size={"20px"} color={theme.palette.common.white} />
                  {!leftSidebarCollapse && (
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        fontWeight: location.pathname === "/home" ? 600 : 500,
                        color: theme.palette.common.white,
                      }}
                    >
                      {t("Home")}
                    </Typography>
                  )}
                </Box>
              </Link>
            </Tooltip>
          </Box>
          {!leftSidebarCollapse && (
            <Divider sx={{ borderColor: theme.palette.sidebar_divider, mx: "-12px", my: "12px", opacity: 0.8 }} />
          )}

          {/* ── MODULES ── */}
          {!leftSidebarCollapse && (
            <Box sx={{ mt: "8px", mb: "4px" }}>
              <Typography
                sx={{
                  px: 2,
                  fontSize: "0.9rem",
                  fontWeight: 500,
                  color: theme.palette.common.white,
                  textAlign: "left",
                }}
              >
                {t("Modules")}
              </Typography>
            </Box>
          )}

          {tabs.map((tab, i) => {
            const isVisible = typeof tab.visibility === "function" ? tab.visibility() : tab.visibility !== false;
            if (!isVisible) return null;
            return (
              <Box key={i}>
                <Tooltip title={leftSidebarCollapse ? t(tab.label) : ""} placement="right">
                  <Link to={tab.path} style={{ textDecoration: "none" }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        paddingY: "10px",
                        paddingX: "14px",
                        borderRadius: "8px",
                        gap: "12px",
                        backgroundColor: location.pathname.startsWith(tab.rootPath) ? theme.palette.primary.main : "transparent",
                        "&:hover": {
                          backgroundColor: location.pathname.startsWith(tab.rootPath) ? theme.palette.primary.main : "rgba(255, 255, 255, 0.05)",
                        },
                      }}
                    >
                      <tab.icon size={"20px"} color={theme.palette.common.white} />
                      {!leftSidebarCollapse && (
                        <Typography
                          sx={{
                            fontSize: "0.9rem",
                            fontWeight: location.pathname.startsWith(tab.rootPath) ? 600 : 500,
                            color: theme.palette.common.white,
                          }}
                        >
                          {t(tab.label)}
                        </Typography>
                      )}
                    </Box>
                  </Link>
                </Tooltip>
              </Box>
            );
          })}
          {!leftSidebarCollapse && (
            <Divider sx={{ borderColor: theme.palette.sidebar_divider, mx: "-12px", mt: "24px", mb: "12px", opacity: 0.8 }} />
          )}

          {/* ── FEATURES ── */}
          {tabs.map((tab, i) => (
            <Box
              key={i}
              sx={{
                display: location.pathname.startsWith(tab.rootPath) ? "flex" : "none",
                flexDirection: "column",
                mt: 2,
                gap: "6px",
              }}
            >
              {!leftSidebarCollapse && (
                <Box sx={{ mb: "4px" }}>
                  <Typography
                    sx={{
                      px: 2,
                      fontSize: "0.9rem",
                      fontWeight: 500,
                      color: theme.palette.common.white,
                      textAlign: "left",
                    }}
                  >
                    {t(tab.subHeader || "Features")}
                  </Typography>
                </Box>
              )}
              {tab.subTabs?.map((s, si) => {
                const isVisible = typeof s.visibility === "function" ? s.visibility() : s.visibility !== false;
                if (!isVisible) return null;
                return (
                  <Box key={si}>
                    <Tooltip title={leftSidebarCollapse ? t(s.label) : ""} placement="right">
                      <Link to={s.path} style={{ textDecoration: "none" }}>
                        <motion.div
                          key={s.pulse}
                          initial={s.pulse > 0 ? { scale: 1, filter: "brightness(1)" } : {}}
                          animate={s.pulse > 0 ? {
                            scale: [1, 1.05, 1],
                            filter: ["brightness(1)", "brightness(1.4)", "brightness(1)"],
                            boxShadow: [
                              "0px 0px 4px rgba(74, 222, 128, 0.4)",
                              "0px 0px 30px rgba(74, 222, 128, 0.8)",
                              "0px 0px 4px rgba(74, 222, 128, 0.4)"
                            ],
                          } : {}}
                          transition={{ duration: 0.7, ease: "easeOut" }}
                          style={{
                            borderRadius: "8px",
                            width: "100%",
                            // Persistent border when showBorder is true
                            border: s.showBorder ? `1.5px solid rgba(74, 222, 128, 0.6)` : "1.5px solid transparent",
                            transition: "border 0.5s ease"
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              paddingY: "10px",
                              paddingX: "14px",
                              borderRadius: "8px",
                              gap: "12px",
                              backgroundColor: location.pathname === s.path ? "rgba(255, 255, 255, 0.1)" : "transparent",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                backgroundColor: "rgba(255, 255, 255, 0.04)",
                              },
                            }}
                          >
                            <s.icon
                              size={"19px"}
                              color={location.pathname === s.path || s.showBorder ? "#4ade80" : theme.palette.common.white}
                            />
                            {!leftSidebarCollapse && (
                              <Typography
                                sx={{
                                  fontSize: "0.85rem",
                                  fontWeight: location.pathname === s.path ? 600 : 500,
                                  color: location.pathname === s.path ? theme.palette.primary.main : theme.palette.common.white,
                                }}
                              >
                                {t(s.label)}
                              </Typography>
                            )}
                          </Box>
                        </motion.div>
                      </Link>
                    </Tooltip>
                  </Box>
                );
              })}
              {!leftSidebarCollapse && (
                <Divider sx={{ borderColor: theme.palette.sidebar_divider, mx: "-12px", mt: "20px", mb: "8px", opacity: 0.8 }} />
              )}
            </Box>
          ))}
        </Box>

        {/* ── FOOTER AREA: EPD LIBRARY BUTTON ── */}
        <Box
          sx={{
            px: "12px",
            py: "10px",
            borderTop: "1px solid rgba(255, 255, 255, 0.06)",
            flexShrink: 0,
          }}
        >
          <Tooltip title={leftSidebarCollapse ? t("EPD Library") : ""} placement="right">
            <Link to={routing.library.epd} style={{ textDecoration: "none" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: leftSidebarCollapse ? "center" : "flex-start",
                  paddingY: "8px",
                  paddingX: leftSidebarCollapse ? "8px" : "12px",
                  borderRadius: "8px",
                  gap: "10px",
                  backgroundColor: location.pathname.startsWith(routing.library.epd)
                    ? theme.palette.primary.main
                    : "transparent",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: location.pathname.startsWith(routing.library.epd)
                      ? theme.palette.primary.main
                      : "rgba(255, 255, 255, 0.06)",
                  },
                }}
              >
                <LuDatabase
                  size={"18px"}
                  color={
                    location.pathname.startsWith(routing.library.epd)
                      ? "#ffffff"
                      : "#4ade80"
                  }
                />
                {!leftSidebarCollapse && (
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      fontWeight: location.pathname.startsWith(routing.library.epd) ? 600 : 500,
                      color: theme.palette.common.white,
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {t("EPD Library")}
                  </Typography>
                )}
              </Box>
            </Link>
          </Tooltip>
        </Box>
      </Box>
    </>
  );
};

export default LeftSidebar;
